import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ShieldCheck, Gem, ChevronDown, ArrowRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { PIERCING_PRICES, PIERCING_GROUPS, formatFt } from "../data/site";

const FAQ = [
  {
    q: "Milyen ékszert használtok?",
    a: "Kizárólag implantátum-minőségű orvosi titán és ellenőrzött forrásból származó ékszereket helyezünk be. Az ékszer ára a piercing díját már tartalmazza az alap kivitelben.",
  },
  {
    q: "Hogyan ápoljam a friss piercinget?",
    a: "Steril fiziológiasó-oldattal, naponta kétszer, tiszta kézzel. Ne forgasd, ne piszkáld, és kerüld a medencét, szaunát a gyógyulási időszakban. A szúrás után részletes, írásos utókezelési útmutatót adunk.",
  },
  {
    q: "Mennyire steril a folyamat?",
    a: "Egyszerhasználatos, lezárt tűkkel dolgozunk, az eszközöket autoklávban sterilizáljuk, a felületet minden vendég előtt fertőtlenítjük. A szúrás pár másodperc — a higiénia viszont kompromisszum nélküli.",
  },
];

export default function Piercing() {
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("Mind");
  const [open, setOpen] = useState(0);

  const filtered = useMemo(
    () =>
      PIERCING_PRICES.filter(
        (p) =>
          (group === "Mind" || p.group === group) &&
          p.name.toLowerCase().includes(query.trim().toLowerCase())
      ),
    [query, group]
  );

  return (
    <div data-testid="piercing-page">
      <PageHeader
        testId="piercing-header"
        motif="ring"
        eyebrow="Piercing Budapest"
        title={<>Precíz, steril, <span className="italic text-[#C04B58]">gyors.</span></>}
        description="Tizenhét piercing-típus, fix és nyilvános árakkal. A szúrást Márk és Jaszper Andrea végzi — tízezer piercing feletti rutinnal."
      />

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <Reveal>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xs">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="piercing-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Keresés: pl. helix, szeptum…"
                className="w-full rounded-full border border-[#222222] bg-[#141414] py-3 pl-11 pr-4 text-sm text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-[#9A3542]"
              />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="piercing-group-filters">
              {PIERCING_GROUPS.map((g) => (
                <button
                  key={g}
                  data-testid={`piercing-filter-${g.toLowerCase()}`}
                  onClick={() => setGroup(g)}
                  className={`rounded-full px-4 py-2 text-xs font-semibold tracking-wide transition-all duration-300 ${
                    group === g
                      ? "bg-[#9A3542] text-white shadow-[0_0_20px_rgba(154,53,66,0.25)]"
                      : "border border-white/10 text-zinc-400 hover:border-[#9A3542] hover:text-white"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="card-dark mt-8 divide-y divide-[#1e1e1e] overflow-hidden" data-testid="piercing-price-list">
          <AnimatePresence initial={false}>
            {filtered.map((p) => (
              <motion.div
                key={p.name}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                data-testid={`piercing-price-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                className="flex items-baseline gap-4 px-6 py-4 transition-colors hover:bg-[#181818] sm:px-8"
              >
                <span className="font-display text-lg font-medium text-white">{p.name}</span>
                <span className="font-mono2 rounded border border-white/10 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                  {p.group}
                </span>
                <span className="price-leader mx-2 flex-1" />
                <span className="font-mono2 text-base font-medium text-[#C04B58]">{formatFt(p.price)}</span>
              </motion.div>
            ))}
          </AnimatePresence>
          {filtered.length === 0 && (
            <p className="px-8 py-10 text-center text-sm text-zinc-500" data-testid="piercing-no-results">
              Nincs találat — próbálj másik kifejezést.
            </p>
          )}
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="card-dark h-full p-8" data-testid="piercing-guarantee">
              <div className="flex items-center gap-3">
                <ShieldCheck size={18} className="text-[#9A3542]" />
                <p className="eyebrow">Sterilitás & ékszer</p>
              </div>
              <h3 className="font-display mt-4 text-2xl font-semibold text-white">Kompromisszum nélküli higiénia</h3>
              <ul className="mt-5 space-y-3 text-sm leading-relaxed text-zinc-400">
                <li className="flex gap-3"><Gem size={15} className="mt-0.5 shrink-0 text-[#9A3542]" /> Implantátum-minőségű orvosi titán alapékszer</li>
                <li className="flex gap-3"><ShieldCheck size={15} className="mt-0.5 shrink-0 text-[#9A3542]" /> Egyszerhasználatos, lezárt tűk és autokláv-sterilizált eszközök</li>
                <li className="flex gap-3"><ChevronDown size={15} className="mt-0.5 shrink-0 text-[#9A3542]" /> Részletes, írásos utókezelési útmutató minden szúrás után</li>
              </ul>
              <Link to="/kapcsolat" data-testid="piercing-booking-btn" className="btn-primary mt-8">
                Piercing időpont <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="card-dark h-full p-8" data-testid="piercing-faq">
              <p className="eyebrow">Utókezelés & tudnivalók</p>
              <div className="mt-5 divide-y divide-[#1e1e1e]">
                {FAQ.map((f, i) => (
                  <div key={i}>
                    <button
                      data-testid={`piercing-faq-${i}`}
                      onClick={() => setOpen(open === i ? -1 : i)}
                      className="flex w-full items-center justify-between py-4 text-left"
                    >
                      <span className="text-sm font-semibold text-white">{f.q}</span>
                      <ChevronDown size={16} className={`shrink-0 text-[#9A3542] transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence initial={false}>
                      {open === i && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <p className="pb-4 text-sm leading-relaxed text-zinc-400">{f.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
